<script setup lang="ts">
import { computed, toRef } from "vue";
import { storeToRefs } from "pinia";
import { Select } from "@/components/episto-ui/select";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
import { useResultsV2PopulationsStore } from "@stores/survey/resultsV2/resultsV2.populations";
import { useCardLocalOptions } from "@/composables/useCardLocalOptions";
import { populationColor } from "@/lib/chartPalettes";
import MetricField from "./CardOptionFields/MetricField.vue";
import ColorToggleField from "./CardOptionFields/ColorToggleField.vue";
import OptionRow from "./CardOptionFields/OptionRow.vue";
import OptionSection from "./CardOptionFields/OptionSection.vue";

const props = defineProps({
  card: { type: Object, required: true },
});

const cardRef = toRef(props, "card");
const cardsStore = useResultsV2CardsStore();
const { localOptions, updateLocalOption } = useCardLocalOptions(cardRef);
const { populations: viewPopulations } = storeToRefs(useResultsV2PopulationsStore());

const DEFAULT_METRIC = { type: "options_share", question_id: null, option_codes: [] };
const metric = computed(() => props.card.content?.metric || DEFAULT_METRIC);

function updateMetric(newMetric) {
  cardsStore.updateCardLocal(props.card.id, {
    content: { ...props.card.content, metric: newMetric },
  });
}

const populationOptions = computed(() => [
  { value: "all", label: "All" },
  ...viewPopulations.value
    .map((p) => ({ value: p.search_group?.token, label: p.search_group?.name }))
    .filter((o) => o.value),
]);

const population = computed({
  get: () => localOptions.value.population || "all",
  set: (val) => updateLocalOption("population", val),
});

const SIZE_OPTIONS = [
  { value: "s", label: "Small" },
  { value: "m", label: "Medium" },
  { value: "l", label: "Large" },
];

const size = computed({
  get: () => localOptions.value.size || "m",
  set: (val) => updateLocalOption("size", val),
});

// Default accent color = the selected population's color (when not customized).
const defaultColor = computed(() => populationColor(population.value, viewPopulations.value));
</script>

<template>
  <OptionSection
    id="data"
    title="Data"
    :count="population !== 'all' ? 1 : 0">
    <MetricField :metric="metric" @update="updateMetric" />

    <OptionRow
      label="Population"
      hint="The population the value is computed on.">
      <Select
        :model-value="population"
        :options="populationOptions"
        @update:model-value="population = $event" />
    </OptionRow>
  </OptionSection>

  <OptionSection
    id="display"
    title="Display"
    :count="[size !== 'm', !!localOptions.color].filter(Boolean).length">
    <OptionRow
      label="Size"
      hint="Size of the number in the card.">
      <Select
        :model-value="size"
        :options="SIZE_OPTIONS"
        @update:model-value="size = $event" />
    </OptionRow>

    <ColorToggleField
      :model-value="localOptions.color ?? null"
      :default-color="defaultColor"
      hint="Accent color of the value. It starts as the color of its population."
      @update:model-value="updateLocalOption('color', $event)" />
  </OptionSection>
</template>
